import React, { useRef, useMemo } from 'react'
import { useSortable } from '@dnd-kit/sortable'
import Answer from 'components/answers/Answer'
import ThinAnswer from 'components/answers/ThinAnswer'
import useOpenRight from 'refactor/hooks/useOpenRight'
import styles from './answer-dnd-wrapper.module.scss'
import cn from 'classnames'
import perms from 'lib/perms'
import { shouldSkipAnswerDndWrapperRender } from './answerDndWrapperMemo'

import useUserHasFeature from 'refactor/hooks/useUserHasFeature'

/**
 * @typedef {Object} AnswerDndWrapperProps
 * @property {any} quest
 * @property {any} [answer]
 * @property {boolean} [isActive]
 * @property {boolean} [isThin]
 * @property {string} [dndId]
 * @property {boolean} [_forceDragging]
 * @property {string} [containerId]
 * @property {string} [panelId]
 * @property {boolean} [isTitle]
 * @property {boolean} [isQuoteGuest]
 * @property {number} [quoteCount]
 * @property {number} [headQuoteCount]
 * @property {boolean} [forbidDrop]
 * @property {boolean} [isLast]
 * @property {boolean} [isFirst]
 * @property {Object} [titleAnswerProps]
 * @property {(item: any) => void} [deleteLocalItem]
 * @property {number} [ix]
 */

export default React.memo(
    /** @param {AnswerDndWrapperProps} props */
    props => {
        const ref = useRef(null)
        const handleElRef = useRef(null)

        const userHasDragAndDropEnabled = useUserHasFeature('drag_and_drop')
        const canEditQuest = perms.amQuestOwner(props.quest) || perms.amQuestAdmin(props.quest)
        const canDrag =
            userHasDragAndDropEnabled &&
            canEditQuest &&
            props.isActive &&
            !props.isThin &&
            // Defense in depth for quote_main union rows: this answer belongs
            // to the source room, not the branch rendering it.
            !props.isQuoteGuest
        // Only show the grip when this wrapper is actually enabling drag.
        const showDragHandle = canDrag

        const { setNodeRef, setActivatorNodeRef, attributes, listeners, isDragging } = useSortable({
            id: props.dndId || props.answer?.id,
            disabled: !canDrag || props._forceDragging,
            data: {
                containerId: props.containerId || `quest-${props.quest.id}`,
                panelId: props.panelId,
                quest_id: props.quest.id,
                initialQuestId: props.quest.id,
                answerId: props.answer?.id,
                answer: props.answer,
                isTitle: !!props.isTitle,
                forbidDropOnQuest: !!props.forbidDrop,
                renderGhost: () => (
                    <div
                        className={cn(styles.tailAnswerContainer, styles.dragGhost)}
                        style={{ opacity: 1 }}
                    >
                        {/* @ts-expect-error - Answer is a JSX class component; spread props untyped across file boundary */}
                        <Answer
                            {...{
                                ...props,
                                answer: props.answer,
                                isThin: false,
                                showDragHandle: true,
                                // Render a slimmer ghost (e.g., hide ThreadInfo for title answers).
                                isDndGhost: true,
                                previewRef: null,
                                dragConnector: null,
                            }}
                        />
                    </div>
                ),
            },
        })

        let dragStyles
        // When dragging:
        // - The list item for the active draggable should become the "placeholder" (invisible).
        // - The DragOverlay renders a separate "ghost" with its own styling.
        if (props._forceDragging) {
            // Ghost styling (DragOverlay)
            dragStyles = {
                opacity: 1,
                border: '1px dashed #999',
            }
        } else if (isDragging) {
            // Placeholder styling (list slot)
            dragStyles = {
                opacity: 0,
            }
        }

        const { openRight } = useOpenRight(props.panelId)

        const onClickTailAnswer = ev => {
            openRight(
                { questId: props.answer.quest_id, answerId: props.answer.id },
                { metricInputEvent: ev },
            )
        }

        return (
            <div>
                {props.isThin ? (
                    <div
                        ref={node => {
                            ref.current = node
                            setNodeRef(node)
                        }}
                        style={dragStyles}
                        className={cn(
                            styles.tailAnswerContainer,
                            props.isLast && styles.isLast,
                            props.isFirst && styles.isFirstContainer,
                        )}
                    >
                        <ThinAnswer
                            answer={props.answer}
                            quest={props.quest}
                            onClick={onClickTailAnswer}
                            contextStyles={cn(styles.thinAnswer, props.isFirst && styles.isFirst)}
                            isLast={props.isLast}
                            isFirst={props.isFirst}
                            streamCardSignals
                        />
                    </div>
                ) : (
                    <div
                        ref={node => {
                            ref.current = node
                            setNodeRef(node)
                        }}
                        style={dragStyles}
                        className={styles.tailAnswerContainer}
                    >
                        <Answer
                            {...{
                                ...props,
                                // merge the titleProps in only if it's a title answer
                                ...(props.isTitle ? props.titleAnswerProps : {}),
                                // This key is necessary to reset stale component state. TODO refactor this so state is not stale/unnecessary.
                                ref: null,
                                previewRef: null,
                                dragConnector: null,
                                showDragHandle: showDragHandle,
                                dragHandleListeners: canDrag ? { ...listeners } : {},
                                dragHandleRefSetter: el => {
                                    handleElRef.current = el
                                    setActivatorNodeRef(el)
                                },
                            }}
                        />
                    </div>
                )}
            </div>
        )
    },
    shouldSkipAnswerDndWrapperRender,
)
